import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../utils/api";
import { API_ENDPOINTS } from "../config/api";
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  getStoredUser,
  setStoredUser,
} from "../utils/auth";

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [user, setUser] = useState(getStoredUser);
  const [loading, setLoading] = useState(Boolean(getAccessToken()));
  // Lets OnboardingPage finish its own claim-credit animation/redirect
  // instead of being yanked away the instant isOnboarded flips to true.
  const [holdOnboardingRedirect, setHoldOnboardingRedirect] = useState(false);

  useEffect(() => {
    if (!getAccessToken()) {
      setLoading(false);
      return;
    }
    api
      .get(API_ENDPOINTS.USER.ME)
      .then(({ data }) => {
        setUser(data);
        setStoredUser(data);
      })
      .catch(() => {
        clearTokens();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Always returns the same success response, even for an already-registered
  // email. Does not log the caller in — call login() next.
  const signup = useCallback(async (email, password) => {
    await api.post(API_ENDPOINTS.USER.SIGNUP, { email, password });
  }, []);

  // Does not log the caller in — the account can't log in until this
  // succeeds. Call login() after.
  const verifyEmail = useCallback(async (email, code) => {
    await api.post(API_ENDPOINTS.USER.VERIFY_EMAIL, { email, code });
  }, []);

  // Always 200 regardless of whether the address exists or is already
  // verified. Issuing a new code invalidates the old one and resets the
  // wrong-attempt count.
  const resendVerification = useCallback(async (email) => {
    await api.post(API_ENDPOINTS.USER.RESEND_VERIFICATION, { email });
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post(API_ENDPOINTS.USER.LOGIN, { email, password });
    setTokens(data.access, data.refresh);
    const { data: me } = await api.get(API_ENDPOINTS.USER.ME);
    setUser(me);
    setStoredUser(me);
    return me;
  }, []);

  // Handles both sign-up and log-in: a new email creates+links the account
  // server-side, an existing one just logs in. Either way the response is
  // the same token pair as login(), so the rest of the flow is identical.
  const loginWithGoogle = useCallback(async (idToken) => {
    const { data } = await api.post(API_ENDPOINTS.USER.GOOGLE, { id_token: idToken });
    setTokens(data.access, data.refresh);
    const { data: me } = await api.get(API_ENDPOINTS.USER.ME);
    setUser(me);
    setStoredUser(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    const refresh = getRefreshToken();
    try {
      if (refresh) {
        await api.post(API_ENDPOINTS.USER.LOGOUT, { refresh });
      }
    } catch {
      // Logout is best-effort client-side — clear local state regardless.
    } finally {
      clearTokens();
      setUser(null);
    }
  }, []);

  // Safe to call more than once: full_name/persona/lang just update on repeat calls.
  const onboard = useCallback(async (fullName, persona, lang) => {
    const { data } = await api.post(API_ENDPOINTS.USER.ONBOARD, {
      full_name: fullName,
      ...(persona ? { persona } : {}),
      ...(lang ? { lang } : {}),
    });
    setUser(data);
    setStoredUser(data);
    return data;
  }, []);

  // First-time onboarding is intentionally separate from onboard(), which is
  // also used to update profile and language settings. This prevents those
  // later edits from trying to create another subscription.
  const completeOnboarding = useCallback(async (fullName, persona, lang) => {
    const { data: onboardedUser } = await api.post(API_ENDPOINTS.USER.ONBOARD, {
      full_name: fullName,
      ...(persona ? { persona } : {}),
      ...(lang ? { lang } : {}),
    });

    const subscriptionPlan = user?.organization ? "organization" : "trial";
    await api.post(API_ENDPOINTS.SUBSCRIPTION.SUBSCRIBE, { plan: subscriptionPlan });

    // Do not expose the onboarded user to route guards until subscription creation
    // succeeds; otherwise the page can unmount between these two requests.
    setUser(onboardedUser);
    setStoredUser(onboardedUser);

    return onboardedUser;
  }, [user?.organization]);

  const value = {
    user,
    loading,
    isAuthenticated: Boolean(user),
    isOnboarded: Boolean(user?.is_onboarded),
    holdOnboardingRedirect,
    setHoldOnboardingRedirect,
    signup,
    verifyEmail,
    resendVerification,
    login,
    loginWithGoogle,
    logout,
    onboard,
    completeOnboarding,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return ctx;
}
